import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { debounce, mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';

const SEARCH_QUERY = 'q';
const MINIMUM_PRICE = 20000;

/* =========================================================
   FACETS FORM
========================================================= */

class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.filterStyle === 'vertical') {
      mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  #onBreakpointChange = (event) => {
    if (!event.matches) return;

    const drawer = document.getElementById('filters-drawer');

    if (
      drawer &&
      'close' in drawer &&
      typeof drawer.close === 'function'
    ) {
      drawer.close();
    }
  };

  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    const newParameters = new URLSearchParams(formData);

    if (newParameters.get('filter.v.price.gte') === '') {
      newParameters.delete('filter.v.price.gte');
    }

    if (newParameters.get('filter.v.price.lte') === '') {
      newParameters.delete('filter.v.price.lte');
    }

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();

    if (searchQuery) {
      newParameters.set(SEARCH_QUERY, searchQuery);
    }

    return newParameters;
  }

  #getSearchQuery() {
    const url = new URL(window.location.href);

    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) {
      throw new Error('Section ID is required');
    }

    return id;
  }

#updateURLHash() {
  const url = new URL(window.location.href);
  const urlParameters = this.createURLParameters();

  url.search = '';

  for (const [param, value] of urlParameters.entries()) {
    url.searchParams.append(param, value);
  }

  url.hash = 'ResultsList';

  history.pushState(
    {
      urlParameters: urlParameters.toString()
    },
    '',
    url.toString()
  );
}

updateFilters = () => {
  // Add #ResultsList immediately
  this.#updateURLHash();

  const renderPromise = this.#updateSection();

  // Make sure #ResultsList remains after Shopify finishes
  // updating the section.
  renderPromise.then(() => {
    const url = new URL(window.location.href);

    if (url.hash !== '#ResultsList') {
      url.hash = 'ResultsList';

      history.replaceState(
        history.state,
        '',
        url.toString()
      );
    }
  });

  if (this.dataset.pageType === 'search') {
    this.#dispatchSearchUpdateEvent(renderPromise);
  } else {
    this.#dispatchCollectionUpdateEvent(renderPromise);
  }
};

  #updateSection() {
  const viewTransition = !this.closest('dialog');

  const renderSection = () => sectionRenderer.renderSection(this.sectionId);

  const restorePriceSlider = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.querySelectorAll('price-facet-component').forEach((slider) => {
          if (typeof slider.refreshSlider === 'function') {
            slider.refreshSlider();
          }
        });
      });
    });
  };

  // Prevent the same section update from triggering multiple renders
  if (this._sectionUpdating) {
    return Promise.resolve();
  }

  this._sectionUpdating = true;

  const finishUpdate = () => {
    restorePriceSlider();

    // Give the new product DOM one stable frame before
    // other observers/apps process it.
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._sectionUpdating = false;
          resolve();
        });
      });
    });
  };

  if (viewTransition) {
    return startViewTransition(() => renderSection(), ['product-grid'])
      .then(finishUpdate)
      .catch((error) => {
        this._sectionUpdating = false;
        throw error;
      });
  }

  return renderSection()
    .then(finishUpdate)
    .catch((error) => {
      this._sectionUpdating = false;
      throw error;
    });
}

  #dispatchSearchUpdateEvent(renderPromise) {
    const query = this.#getSearchQuery();

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = SearchUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: SearchUpdateEvent.getSortKey()
        },
        promise: deferredPromise.promise
      })
    );

    renderPromise
      .then(() =>
        deferredPromise.resolve({
          totalCount: this.#getResultsCount()
        })
      )
      .catch((error) =>
        deferredPromise.reject(error)
      );
  }

  #getResultsCount() {
    return Number(this.dataset.resultsCount) || 0;
  }

updateFiltersByURL(url) {
  const newUrl = new URL(url, window.location.origin);

  // Keep the results section as the URL anchor
  newUrl.hash = 'ResultsList';

  history.pushState('', '', newUrl.toString());

  const renderPromise = this.#updateSection();

  if (this.dataset.pageType === 'search') {
    this.#dispatchSearchUpdateEvent(renderPromise);
  } else {
    this.#dispatchCollectionUpdateEvent(renderPromise);
  }
}

  #dispatchCollectionUpdateEvent(renderPromise) {
    const rawId = this.dataset.collectionId;

    const collectionData = {
      id:
        rawId && /^\d+$/.test(rawId)
          ? rawId
          : null,

      handle: this.dataset.collectionHandle,

      productsCount: this.#getResultsCount()
    };

    const deferredPromise =
      CollectionUpdateEvent.createPromise();

    const productFilters =
      CollectionUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new CollectionUpdateEvent({
        collection: collectionData,

        ...(productFilters && {
          productFilters
        }),

        sortKey:
          CollectionUpdateEvent.getSortKey(),

        promise:
          deferredPromise.promise
      })
    );

    renderPromise
      .then(() =>
        deferredPromise.resolve({
          productsCount:
            this.#getResultsCount()
        })
      )
      .catch((error) =>
        deferredPromise.reject(error)
      );
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define(
    'facets-form-component',
    FacetsFormComponent
  );
}


/* =========================================================
   FACET INPUTS
========================================================= */

class FacetInputsComponent extends Component {

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'change',
      this.#handleChange
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(
      'change',
      this.#handleChange
    );
  }

  get sectionId() {
    const id =
      this.closest('.shopify-section')?.id;

    if (!id) {
      throw new Error(
        'FacetInputs component must be a child of a section'
      );
    }

    return id;
  }

  #handleChange = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (
      target.type !== 'checkbox' &&
      target.type !== 'radio'
    ) {
      return;
    }

    if (target.disabled) return;

    this.updateFilters();
  };

  updateFilters() {
    const facetsForm =
      this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) {
      return;
    }

    facetsForm.updateFilters();

    this.#updateSelectedFacetSummary();
  }

  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const closestInput =
      event.target.querySelector('input');

    if (
      !(closestInput instanceof HTMLInputElement)
    ) {
      return;
    }

    if (
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();

      closestInput.checked =
        !closestInput.checked;

      this.updateFilters();
    }
  }

  prefetchPageImmediate(event) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    this.#prefetchOption(event.target);
  }

  #prefetchOption(optionElement) {
    const form =
      this.closest('form');

    if (!form) return;

    const inputElement =
      optionElement.querySelector('input');

    if (
      !(inputElement instanceof HTMLInputElement) ||
      inputElement.disabled
    ) {
      return;
    }

    const facetsForm =
      this.closest('facets-form-component');

    if (
      !(facetsForm instanceof FacetsFormComponent)
    ) {
      return;
    }

    const formData =
      new FormData(form);

    if (!inputElement.checked) {
      formData.append(
        inputElement.name,
        inputElement.value
      );
    }

    const urlParameters =
      facetsForm.createURLParameters(formData);

    const url = new URL(
      window.location.pathname,
      window.location.origin
    );

    for (const [key, value] of urlParameters) {
      url.searchParams.append(
        key,
        value
      );
    }

    if (inputElement.checked) {
      url.searchParams.delete(
        inputElement.name,
        inputElement.value
      );
    }

    sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      url
    );
  }

  prefetchPage = debounce(
    (event) => {
      if (
        !(event.target instanceof HTMLElement)
      ) {
        return;
      }

      this.#prefetchOption(
        event.target
      );
    },
    200
  );

  cancelPrefetchPage = () => {
    this.prefetchPage.cancel();
  };

  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) {
      return;
    }

    const checkedInputElements =
      this.refs.facetInputs.filter(
        (input) => input.checked
      );

    const details =
      this.closest('details');

    const statusComponent =
      details?.querySelector(
        'facet-status-component'
      );

    if (
      !(statusComponent instanceof FacetStatusComponent)
    ) {
      return;
    }

    statusComponent.updateListSummary(
      checkedInputElements
    );
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define(
    'facet-inputs-component',
    FacetInputsComponent
  );
}


/* =========================================================
   PRICE FACET
/* =========================================================
   PRICE FACET
========================================================= */

class PriceFacetComponent extends Component {
  currency;
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeyDown);

    this.currency = this.dataset.currency || 'INR';

    this.moneyFormat = this.#extractMoneyPlaceholder(
      this.dataset.moneyFormat || '{{amount}}'
    );

    requestAnimationFrame(() => {
      this.initSlider();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /* =====================================================
     REFRESH AFTER SHOPIFY RE-RENDERS
  ===================================================== */

  refreshSlider() {
    requestAnimationFrame(() => {
      this.initSlider();
    });
  }

  /* =====================================================
     MONEY FORMAT
  ===================================================== */

  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);

    return match ? match[0] : '{{amount}}';
  }

  /* =====================================================
     KEYBOARD VALIDATION
  ===================================================== */

  #onKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey) {
      return;
    }

    const allowedKeys = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '.',
      ',',
      ' ',
      'Tab',
      'Backspace',
      'Enter',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Escape'
    ];

    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  /* =====================================================
     CLEAN PRICE VALUE
  ===================================================== */

  #cleanPriceValue(value) {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const clean = String(value)
      .replace(/[^\d.-]/g, '');

    const number = Number(clean);

    return Number.isFinite(number)
      ? number
      : null;
  }

  /* =====================================================
     GET SLIDER MAXIMUM
  ===================================================== */

  #getSliderMaximum() {
    const maxSlider = this.querySelector('.range-max');

    if (!(maxSlider instanceof HTMLInputElement)) {
      return 0;
    }

    return Number(maxSlider.max) || 0;
  }

  /* =====================================================
     ERROR MESSAGE
     
     IMPORTANT:
     Uses existing .price-error-message
  ===================================================== */

  #getErrorMessage() {
    return this.querySelector('.price-error-message');
  }

  #showPriceError(input) {
    const errorMessage = this.#getErrorMessage();

    /*
     * Remove error from both first.
     * Then add it ONLY to the invalid input.
     */
    this.#removeInputErrors();

    if (input instanceof HTMLInputElement) {
      input.classList.add('price-input-error');
    }

    if (errorMessage) {
      errorMessage.classList.add('active');
      errorMessage.style.display = 'block';
    }
  }

  #hidePriceError() {
    const errorMessage = this.#getErrorMessage();

    this.#removeInputErrors();

    if (errorMessage) {
      errorMessage.classList.remove('active');
      errorMessage.style.display = 'none';
    }
  }

  #removeInputErrors() {
    const {
      minInput,
      maxInput
    } = this.refs;

    if (minInput instanceof HTMLInputElement) {
      minInput.classList.remove('price-input-error');
    }

    if (maxInput instanceof HTMLInputElement) {
      maxInput.classList.remove('price-input-error');
    }
  }

  /* =====================================================
     VALIDATE PRICE INPUTS
     
     Returns:
     {
       valid: true/false,
       minValue,
       maxValue,
       minInvalid,
       maxInvalid
     }
  ===================================================== */

  #validatePriceInputs() {
    const {
      minInput,
      maxInput
    } = this.refs;

    if (
      !(minInput instanceof HTMLInputElement) ||
      !(maxInput instanceof HTMLInputElement)
    ) {
      return {
        valid: false,
        minValue: null,
        maxValue: null,
        minInvalid: false,
        maxInvalid: false
      };
    }

    const minimumPrice = 20000;

    const minValue = this.#cleanPriceValue(
      minInput.value
    );

    const maxValue = this.#cleanPriceValue(
      maxInput.value
    );

    const minInvalid =
      minValue !== null &&
      minValue < minimumPrice;

    const maxInvalid =
      maxValue !== null &&
      maxValue < minimumPrice;

    /*
     * BOTH INVALID
     */
    if (minInvalid && maxInvalid) {
      this.#removeInputErrors();

      minInput.classList.add('price-input-error');
      maxInput.classList.add('price-input-error');

      const errorMessage = this.#getErrorMessage();

      if (errorMessage) {
        errorMessage.classList.add('active');
        errorMessage.style.display = 'block';
      }

      return {
        valid: false,
        minValue,
        maxValue,
        minInvalid: true,
        maxInvalid: true
      };
    }

    /*
     * ONLY MIN INVALID
     */
    if (minInvalid) {
      this.#showPriceError(minInput);

      return {
        valid: false,
        minValue,
        maxValue,
        minInvalid: true,
        maxInvalid: false
      };
    }

    /*
     * ONLY MAX INVALID
     */
    if (maxInvalid) {
      this.#showPriceError(maxInput);

      return {
        valid: false,
        minValue,
        maxValue,
        minInvalid: false,
        maxInvalid: true
      };
    }

    /*
     * EVERYTHING VALID
     */
    this.#hidePriceError();

    return {
      valid: true,
      minValue,
      maxValue,
      minInvalid: false,
      maxInvalid: false
    };
  }

  /* =====================================================
     UPDATE PRICE FILTER + RESULTS
  ===================================================== */

  updatePriceFilterAndResults() {
    const {
      minInput,
      maxInput
    } = this.refs;

    if (
      !(minInput instanceof HTMLInputElement) ||
      !(maxInput instanceof HTMLInputElement)
    ) {
      return;
    }

    const minimumPrice = 20000;

    /*
     * IMPORTANT:
     * Validate BEFORE changing anything.
     *
     * This prevents:
     * 4000 -> 20000
     */
    const validation =
      this.#validatePriceInputs();

    if (!validation.valid) {
      /*
       * DO NOT:
       * - change input value
       * - change slider
       * - update URL
       * - call Shopify filter
       */
      return;
    }

    let minValue = validation.minValue;
    let maxValue = validation.maxValue;

    /*
     * Empty MIN:
     * internally use 20,000
     */
    if (minValue === null) {
      minValue = minimumPrice;
    }

    /*
     * Empty MAX:
     * use slider maximum
     */
    if (maxValue === null) {
      maxValue = this.#getSliderMaximum();
    }

    /*
     * Keep values within valid range.
     */
    minValue = Math.max(
      minimumPrice,
      minValue
    );

    maxValue = Math.max(
      minimumPrice,
      maxValue
    );

    /*
     * If MIN > MAX,
     * don't automatically change user's entered
     * MIN value.
     *
     * Instead reset the other side to slider maximum.
     */
    if (minValue > maxValue) {
      maxValue = this.#getSliderMaximum();
    }

    if (maxValue < minValue) {
      minValue = minimumPrice;
    }

    /*
     * Update actual inputs only AFTER validation.
     */
    minInput.value = String(minValue);
    maxInput.value = String(maxValue);

    minInput.name = 'filter.v.price.gte';
    maxInput.name = 'filter.v.price.lte';

    /*
     * Sync sliders.
     */
    this.#syncSlidersFromInputs();

    this.#updateSliderRange();

    /*
     * Apply Shopify filter.
     */
    const facetsForm =
      this.closest('facets-form-component');

    if (
      !(facetsForm instanceof FacetsFormComponent)
    ) {
      return;
    }

    facetsForm.updateFilters();

    this.#setMinAndMaxValues();

    this.#updateSummary();
  }

  /* =====================================================
     SET MIN/MAX ATTRIBUTES
  ===================================================== */

  #setMinAndMaxValues() {
    const {
      minInput,
      maxInput
    } = this.refs;

    if (
      !(minInput instanceof HTMLInputElement) ||
      !(maxInput instanceof HTMLInputElement)
    ) {
      return;
    }

    const minimumPrice = 20000;

    minInput.setAttribute(
      'data-min',
      String(minimumPrice)
    );

    maxInput.setAttribute(
      'data-min',
      String(minimumPrice)
    );
  }

  /* =====================================================
     PRICE SUMMARY
  ===================================================== */

  #updateSummary() {
    const {
      minInput,
      maxInput
    } = this.refs;

    const details = this.closest('details');

    const statusComponent =
      details?.querySelector(
        'facet-status-component'
      );

    if (
      !(statusComponent instanceof FacetStatusComponent)
    ) {
      return;
    }

    statusComponent.updatePriceSummary(
      minInput,
      maxInput
    );
  }

  /* =====================================================
     UPDATE BLACK SLIDER RANGE
  ===================================================== */

  #updateSliderRange() {
    const minSlider =
      this.querySelector('.range-min');

    const maxSlider =
      this.querySelector('.range-max');

    const range =
      this.querySelector('.slider-range');

    if (
      !(minSlider instanceof HTMLInputElement) ||
      !(maxSlider instanceof HTMLInputElement) ||
      !(range instanceof HTMLElement)
    ) {
      return;
    }

    const sliderMin =
      Number(minSlider.min) || 20000;

    const sliderMax =
      Number(maxSlider.max);

    const minValue =
      Number(minSlider.value);

    const maxValue =
      Number(maxSlider.value);

    if (
      !Number.isFinite(sliderMax) ||
      !Number.isFinite(minValue) ||
      !Number.isFinite(maxValue) ||
      sliderMax <= sliderMin
    ) {
      return;
    }

    const total =
      sliderMax - sliderMin;

    let left =
      ((minValue - sliderMin) / total) * 100;

    let right =
      100 -
      ((maxValue - sliderMin) / total) * 100;

    left = Math.max(
      0,
      Math.min(100, left)
    );

    right = Math.max(
      0,
      Math.min(100, right)
    );

    range.style.setProperty(
      'left',
      `${left}%`,
      'important'
    );

    range.style.setProperty(
      'right',
      `${right}%`,
      'important'
    );

    range.style.setProperty(
      'background',
      '#151819',
      'important'
    );

    range.style.setProperty(
      'height',
      '4px',
      'important'
    );

    range.style.setProperty(
      'border-radius',
      '10px',
      'important'
    );

    range.style.setProperty(
      'position',
      'absolute',
      'important'
    );

    range.style.setProperty(
      'top',
      '50%',
      'important'
    );

    range.style.setProperty(
      'transform',
      'translateY(-50%)',
      'important'
    );

    range.style.setProperty(
      'z-index',
      '2',
      'important'
    );

    range.style.setProperty(
      'pointer-events',
      'none',
      'important'
    );
  }

  /* =====================================================
     CUSTOM RANGE SLIDER
  ===================================================== */

  initSlider() {
    const minSlider =
      this.querySelector('.range-min');

    const maxSlider =
      this.querySelector('.range-max');

    const minInput =
      this.refs.minInput;

    const maxInput =
      this.refs.maxInput;

    if (
      !(minSlider instanceof HTMLInputElement) ||
      !(maxSlider instanceof HTMLInputElement) ||
      !(minInput instanceof HTMLInputElement) ||
      !(maxInput instanceof HTMLInputElement)
    ) {
      return;
    }

    const sliderMin = 20000;

    const sliderMax =
      Number(maxSlider.max);

    if (
      !Number.isFinite(sliderMax) ||
      sliderMax <= sliderMin
    ) {
      return;
    }

    /*
     * Set slider limits.
     */

    minSlider.min =
      String(sliderMin);

    minSlider.max =
      String(sliderMax);

    maxSlider.min =
      String(sliderMin);

    maxSlider.max =
      String(sliderMax);

    /*
     * Read URL.
     */

    const url =
      new URL(window.location.href);

    const urlMin =
      url.searchParams.get(
        'filter.v.price.gte'
      );

    const urlMax =
      url.searchParams.get(
        'filter.v.price.lte'
      );

    let currentMin =
      urlMin !== null &&
      urlMin !== ''
        ? Number(urlMin)
        : sliderMin;

    let currentMax =
      urlMax !== null &&
      urlMax !== ''
        ? Number(urlMax)
        : sliderMax;

    if (!Number.isFinite(currentMin)) {
      currentMin = sliderMin;
    }

    if (!Number.isFinite(currentMax)) {
      currentMax = sliderMax;
    }

    currentMin = Math.max(
      sliderMin,
      Math.min(currentMin, sliderMax)
    );

    currentMax = Math.max(
      sliderMin,
      Math.min(currentMax, sliderMax)
    );

    if (currentMin > currentMax) {
      currentMin = sliderMin;
      currentMax = sliderMax;
    }

    /*
     * Set slider values.
     */

    minSlider.value =
      String(currentMin);

    maxSlider.value =
      String(currentMax);

    /*
     * Set text input values.
     *
     * IMPORTANT:
     * Do NOT force 20,000 into an empty input.
     */

    if (
      urlMin !== null &&
      urlMin !== ''
    ) {
      minInput.value =
        String(currentMin);
    } else {
      minInput.value = '';
    }

    if (
      urlMax !== null &&
      urlMax !== ''
    ) {
      maxInput.value =
        String(currentMax);
    } else {
      maxInput.value = '';
    }

    /*
     * Clear old error after Shopify render.
     */
    this.#hidePriceError();

    /*
     * Restore range.
     */
    this.#updateSliderRange();

    requestAnimationFrame(() => {
      this.#updateSliderRange();

      requestAnimationFrame(() => {
        this.#updateSliderRange();
      });
    });

    /* ===================================================
       MIN SLIDER INPUT
    =================================================== */

    minSlider.addEventListener(
      'input',
      () => {
        let value =
          Number(minSlider.value);

        const maxValue =
          Number(maxSlider.value);

        value = Math.max(
          sliderMin,
          value
        );

        value = Math.min(
          value,
          maxValue
        );

        minSlider.value =
          String(value);

        minInput.value =
          String(value);

        /*
         * Slider itself is always >= 20,000,
         * therefore remove error.
         */
        minInput.classList.remove(
          'price-input-error'
        );

        this.#updateSliderRange();
      }
    );

    /* ===================================================
       MAX SLIDER INPUT
    =================================================== */

    maxSlider.addEventListener(
      'input',
      () => {
        let value =
          Number(maxSlider.value);

        const minValue =
          Number(minSlider.value);

        value = Math.max(
          sliderMin,
          value
        );

        value = Math.max(
          value,
          minValue
        );

        value = Math.min(
          value,
          sliderMax
        );

        maxSlider.value =
          String(value);

        maxInput.value =
          String(value);

        maxInput.classList.remove(
          'price-input-error'
        );

        this.#updateSliderRange();
      }
    );

    /* ===================================================
       MIN SLIDER CHANGE
    =================================================== */

    minSlider.addEventListener(
      'change',
      () => {
        let value =
          Number(minSlider.value);

        const maxValue =
          Number(maxSlider.value);

        value = Math.max(
          sliderMin,
          value
        );

        if (value > maxValue) {
          value = maxValue;

          minSlider.value =
            String(value);
        }

        minInput.value =
          String(value);

        this.#updateSliderRange();

        this.updatePriceFilterAndResults();
      }
    );

    /* ===================================================
       MAX SLIDER CHANGE
    =================================================== */

    maxSlider.addEventListener(
      'change',
      () => {
        let value =
          Number(maxSlider.value);

        const minValue =
          Number(minSlider.value);

        value = Math.max(
          sliderMin,
          value
        );

        if (value < minValue) {
          value = minValue;

          maxSlider.value =
            String(value);
        }

        value = Math.min(
          value,
          sliderMax
        );

        maxSlider.value =
          String(value);

        maxInput.value =
          String(value);

        this.#updateSliderRange();

        this.updatePriceFilterAndResults();
      }
    );

    /* ===================================================
       MANUAL MIN INPUT
       
       IMPORTANT:
       DO NOT sync 4000 -> slider -> 20000
       before validation.
    =================================================== */

    minInput.addEventListener(
      'change',
      () => {
        const value =
          this.#cleanPriceValue(
            minInput.value
          );

        /*
         * Empty value.
         */
        if (value === null) {
          minInput.classList.remove(
            'price-input-error'
          );

          this.#hidePriceError();

          this.#updateSliderRange();

          this.updatePriceFilterAndResults();

          return;
        }

        /*
         * INVALID:
         * Keep 4000 exactly as entered.
         *
         * DO NOT sync slider.
         */
        if (value < sliderMin) {
          this.#showPriceError(
            minInput
          );

          return;
        }

        /*
         * VALID:
         * Now sync to slider.
         */
        this.#hidePriceError();

        this.#syncInputToSlider(
          minInput,
          minSlider,
          sliderMin,
          sliderMax
        );

        const minValue =
          Number(minSlider.value);

        const maxValue =
          Number(maxSlider.value);

        if (minValue > maxValue) {
          maxSlider.value =
            String(sliderMax);

          maxInput.value =
            String(sliderMax);
        }

        this.#updateSliderRange();

        this.updatePriceFilterAndResults();
      }
    );

    /* ===================================================
       MANUAL MAX INPUT
       
       IMPORTANT:
       DO NOT sync 4000 -> slider -> 20000
       before validation.
    =================================================== */

    maxInput.addEventListener(
      'change',
      () => {
        const value =
          this.#cleanPriceValue(
            maxInput.value
          );

        /*
         * Empty value.
         */
        if (value === null) {
          maxInput.classList.remove(
            'price-input-error'
          );

          this.#hidePriceError();

          this.#updateSliderRange();

          this.updatePriceFilterAndResults();

          return;
        }

        /*
         * INVALID:
         * Keep 4000 exactly as entered.
         *
         * DO NOT sync slider.
         */
        if (value < sliderMin) {
          this.#showPriceError(
            maxInput
          );

          return;
        }

        /*
         * VALID:
         * Now sync to slider.
         */
        this.#hidePriceError();

        this.#syncInputToSlider(
          maxInput,
          maxSlider,
          sliderMin,
          sliderMax
        );

        const minValue =
          Number(minSlider.value);

        const maxValue =
          Number(maxSlider.value);

        if (maxValue < minValue) {
          minSlider.value =
            String(sliderMin);

          minInput.value =
            String(sliderMin);
        }

        this.#updateSliderRange();

        this.updatePriceFilterAndResults();
      }
    );
  }

  /* =====================================================
     SYNC INPUT → SLIDER
  ===================================================== */

  #syncInputToSlider(
    input,
    slider,
    min,
    max
  ) {
    if (
      !(input instanceof HTMLInputElement) ||
      !(slider instanceof HTMLInputElement)
    ) {
      return;
    }

    if (!input.value.trim()) {
      return;
    }

    const value =
      this.#cleanPriceValue(
        input.value
      );

    if (value === null) {
      return;
    }

    /*
     * At this point validation has already happened.
     */
    const safeValue =
      Math.max(
        min,
        Math.min(max, value)
      );

    slider.min =
      String(min);

    slider.max =
      String(max);

    slider.value =
      String(safeValue);

    input.value =
      String(safeValue);
  }

  /* =====================================================
     SYNC SLIDERS FROM INPUTS
  ===================================================== */

  #syncSlidersFromInputs() {
    const minSlider =
      this.querySelector('.range-min');

    const maxSlider =
      this.querySelector('.range-max');

    const minInput =
      this.refs.minInput;

    const maxInput =
      this.refs.maxInput;

    if (
      !(minSlider instanceof HTMLInputElement) ||
      !(maxSlider instanceof HTMLInputElement) ||
      !(minInput instanceof HTMLInputElement) ||
      !(maxInput instanceof HTMLInputElement)
    ) {
      return;
    }

    const minimumPrice = 20000;

    const sliderMax =
      Number(maxSlider.max);

    let minValue =
      this.#cleanPriceValue(
        minInput.value
      );

    let maxValue =
      this.#cleanPriceValue(
        maxInput.value
      );

    if (minValue === null) {
      minValue = minimumPrice;
    }

    if (maxValue === null) {
      maxValue = sliderMax;
    }

    minValue = Math.max(
      minimumPrice,
      Math.min(
        minValue,
        sliderMax
      )
    );

    maxValue = Math.max(
      minimumPrice,
      Math.min(
        maxValue,
        sliderMax
      )
    );

    if (minValue > maxValue) {
      minValue = minimumPrice;
      maxValue = sliderMax;
    }

    minSlider.min =
      String(minimumPrice);

    maxSlider.min =
      String(minimumPrice);

    minSlider.value =
      String(minValue);

    maxSlider.value =
      String(maxValue);

    this.#updateSliderRange();
  }
}

if (
  !customElements.get(
    'price-facet-component'
  )
) {
  customElements.define(
    'price-facet-component',
    PriceFacetComponent
  );
}

/* =========================================================
   CLEAR FILTER
========================================================= */

class FacetClearComponent extends Component {

  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'keyup',
      this.#handleKeyUp
    );

    document.addEventListener(
      StandardEvents.searchUpdate,
      this.#handleFilterUpdate
    );

    document.addEventListener(
      StandardEvents.collectionUpdate,
      this.#handleFilterUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.searchUpdate,
      this.#handleFilterUpdate
    );

    document.removeEventListener(
      StandardEvents.collectionUpdate,
      this.#handleFilterUpdate
    );
  }

  clearFilter(event) {
    if (
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    if (
      event instanceof KeyboardEvent
    ) {
      if (
        event.key !== 'Enter' &&
        event.key !== ' '
      ) {
        return;
      }

      event.preventDefault();
    }

    const container =
      event.target.closest(
        'facet-inputs-component, price-facet-component'
      );

    if (container) {

      container
        .querySelectorAll(
          '[type="checkbox"]:checked, input'
        )
        .forEach((input) => {

          if (
            input instanceof HTMLInputElement
          ) {
            input.checked = false;
            input.value = '';
          }
        });

      const minSlider =
        container.querySelector(
          '.range-min'
        );

      const maxSlider =
        container.querySelector(
          '.range-max'
        );

      if (
        minSlider instanceof HTMLInputElement
      ) {
        minSlider.value =
          minSlider.min || '0';
      }

      if (
        maxSlider instanceof HTMLInputElement
      ) {
        maxSlider.value =
          maxSlider.max || '0';
      }

      const range =
        container.querySelector(
          '.slider-range'
        );

      if (range) {
        range.style.left = '0%';
        range.style.right = '0%';
      }

      /*
       * Remove price error state.
       */
      container
        .querySelectorAll(
          '.price-input-error'
        )
        .forEach((input) => {
          input.classList.remove(
            'price-input-error'
          );
        });

      const priceError =
        container.querySelector(
          '.price-error-message'
        );

      if (priceError) {
        priceError.classList.remove(
          'active'
        );

        priceError.setAttribute(
          'hidden',
          ''
        );
      }
    }

    const details =
      event.target.closest('details');

    const statusComponent =
      details?.querySelector(
        'facet-status-component'
      );

    if (
      statusComponent instanceof FacetStatusComponent
    ) {
      statusComponent.clearSummary();
    }

    const facetsForm =
      this.closest(
        'facets-form-component'
      );

    if (
      !(facetsForm instanceof FacetsFormComponent)
    ) {
      return;
    }

    facetsForm.updateFilters();
  }

  #handleKeyUp = (event) => {
    if (event.metaKey) return;

    if (event.key === 'Enter') {
      this.clearFilter(event);
    }
  };

  #handleFilterUpdate = (event) => {
    const eventSection =
      event.target?.closest(
        '.shopify-section'
      );

    const mySection =
      this.closest(
        '.shopify-section'
      );

    if (
      eventSection &&
      mySection &&
      eventSection !== mySection
    ) {
      return;
    }

    const {
      clearButton
    } = this.refs;

    if (
      clearButton instanceof Element
    ) {

      const filters =
        event instanceof SearchUpdateEvent
          ? event.search?.productFilters ?? []
          : event.productFilters ?? [];

      clearButton.classList.toggle(
        'facets__clear--active',
        filters.length > 0
      );
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define(
    'facet-clear-component',
    FacetClearComponent
  );
}


/* =========================================================
   REMOVE FILTER
========================================================= */

class FacetRemoveComponent extends Component {

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.searchUpdate,
      this.#handleFilterUpdate
    );

    document.addEventListener(
      StandardEvents.collectionUpdate,
      this.#handleFilterUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.searchUpdate,
      this.#handleFilterUpdate
    );

    document.removeEventListener(
      StandardEvents.collectionUpdate,
      this.#handleFilterUpdate
    );
  }

  removeFilter({ form }, event) {

    if (
      event instanceof KeyboardEvent
    ) {

      if (
        event.key !== 'Enter' &&
        event.key !== ' '
      ) {
        return;
      }

      event.preventDefault();
    }

    const url =
      this.dataset.url;

    if (!url) return;

    const facetsForm =
      form
        ? document.getElementById(form)
        : this.closest(
            'facets-form-component'
          );

    if (
      !(facetsForm instanceof FacetsFormComponent)
    ) {
      return;
    }

    facetsForm.updateFiltersByURL(
      url
    );
  }

  #handleFilterUpdate = (event) => {

    const eventSection =
      event.target?.closest(
        '.shopify-section'
      );

    const mySection =
      this.closest(
        '.shopify-section'
      );

    if (
      eventSection &&
      mySection &&
      eventSection !== mySection
    ) {
      return;
    }

    const {
      clearButton
    } = this.refs;

    if (
      clearButton instanceof Element
    ) {

      const activeClass =
        this.getAttribute(
          'active-class'
        ) || 'active';

      const filters =
        event instanceof SearchUpdateEvent
          ? event.search?.productFilters ?? []
          : event.productFilters ?? [];

      clearButton.classList.toggle(
        activeClass,
        filters.length > 0
      );
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define(
    'facet-remove-component',
    FacetRemoveComponent
  );
}


/* =========================================================
   SORTING
========================================================= */

class SortingFilterComponent extends Component {

  requiredRefs = [
    'details',
    'summary',
    'listbox'
  ];

  handleKeyDown = (event) => {

    const {
      listbox
    } = this.refs;

    if (!(listbox instanceof Element)) {
      return;
    }

    const options =
      Array.from(
        listbox.querySelectorAll(
          '[role="option"]'
        )
      );

    const currentFocused =
      options.find(
        (option) =>
          option instanceof HTMLElement &&
          option.tabIndex === 0
      );

    let newFocusIndex =
      currentFocused
        ? options.indexOf(currentFocused)
        : 0;

    switch (event.key) {

      case 'ArrowDown':

        event.preventDefault();

        newFocusIndex =
          Math.min(
            newFocusIndex + 1,
            options.length - 1
          );

        this.#moveFocus(
          options,
          newFocusIndex
        );

        break;

      case 'ArrowUp':

        event.preventDefault();

        newFocusIndex =
          Math.max(
            newFocusIndex - 1,
            0
          );

        this.#moveFocus(
          options,
          newFocusIndex
        );

        break;

      case 'Enter':
      case ' ':

        if (
          event.target instanceof Element
        ) {

          const targetOption =
            event.target.closest(
              '[role="option"]'
            );

          if (targetOption) {

            event.preventDefault();

            this.#selectOption(
              targetOption
            );
          }
        }

        break;

      case 'Escape':

        event.preventDefault();

        this.#closeDropdown();

        break;
    }
  };

  handleToggle = () => {

    const {
      details,
      summary,
      listbox
    } = this.refs;

    if (
      !(details instanceof HTMLDetailsElement) ||
      !(summary instanceof HTMLElement)
    ) {
      return;
    }

    const isOpen =
      details.open;

    summary.setAttribute(
      'aria-expanded',
      isOpen.toString()
    );

    if (
      isOpen &&
      listbox instanceof Element
    ) {

      const selectedOption =
        listbox.querySelector(
          '[aria-selected="true"]'
        );

      if (
        selectedOption instanceof HTMLElement
      ) {
        selectedOption.focus();
      }
    }
  };

  #moveFocus(options, newIndex) {

    options.forEach(
      (option) => {

        if (
          option instanceof HTMLElement
        ) {
          option.tabIndex = -1;
        }
      }
    );

    const targetOption =
      options[newIndex];

    if (
      targetOption instanceof HTMLElement
    ) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  #selectOption(option) {

    const input =
      option.querySelector(
        'input[type="radio"]'
      );

    if (
      input instanceof HTMLInputElement &&
      option instanceof HTMLElement
    ) {

      this
        .querySelectorAll(
          '[role="option"]'
        )
        .forEach(
          (opt) =>
            opt.setAttribute(
              'aria-selected',
              'false'
            )
        );

      option.setAttribute(
        'aria-selected',
        'true'
      );

      input.click();

      this.#closeDropdown();
    }
  }

  #closeDropdown() {

    const {
      details,
      summary
    } = this.refs;

    if (
      details instanceof HTMLDetailsElement
    ) {

      const options =
        this.querySelectorAll(
          '[role="option"]'
        );

      const selectedOption =
        this.querySelector(
          '[aria-selected="true"]'
        );

      options.forEach(
        (opt) => {

          if (
            opt instanceof HTMLElement
          ) {
            opt.tabIndex = -1;
          }
        }
      );

      if (
        selectedOption instanceof HTMLElement
      ) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;

      if (
        summary instanceof HTMLElement
      ) {
        summary.focus();
      }
    }
  }

  updateFilterAndSorting(event) {

    const facetsForm =
      this.closest(
        'facets-form-component'
      ) ||
      this.closest(
        '.shopify-section'
      )?.querySelector(
        'facets-form-component'
      );

    if (
      !(facetsForm instanceof FacetsFormComponent)
    ) {
      return;
    }

    const isMobile =
      window.innerWidth < 750;

    const shouldDisable =
      this.dataset.shouldUseSelectOnMobile ===
      'true';

    if (shouldDisable) {

      if (isMobile) {

        this
          .querySelectorAll(
            'input[name="sort_by"]'
          )
          .forEach(
            (input) => {

              if (
                input instanceof HTMLInputElement
              ) {
                input.disabled = true;
              }
            }
          );

      } else {

        const selectElement =
          this.querySelector(
            'select[name="sort_by"]'
          );

        if (
          selectElement instanceof HTMLSelectElement
        ) {
          selectElement.disabled = true;
        }
      }
    }

    facetsForm.updateFilters();

    this.updateFacetStatus(event);

    if (shouldDisable) {

      if (isMobile) {

        this
          .querySelectorAll(
            'input[name="sort_by"]'
          )
          .forEach(
            (input) => {

              if (
                input instanceof HTMLInputElement
              ) {
                input.disabled = false;
              }
            }
          );

      } else {

        const selectElement =
          this.querySelector(
            'select[name="sort_by"]'
          );

        if (
          selectElement instanceof HTMLSelectElement
        ) {
          selectElement.disabled = false;
        }
      }
    }

    const {
      details
    } = this.refs;

    if (
      details instanceof HTMLDetailsElement
    ) {
      details.open = false;
    }
  }

  updateFacetStatus(event) {

    if (
      !(event.target instanceof HTMLSelectElement)
    ) {
      return;
    }

    const details =
      this.querySelector('details');

    if (!details) return;

    const facetStatus =
      details.querySelector(
        'facet-status-component'
      );

    if (
      !(facetStatus instanceof FacetStatusComponent)
    ) {
      return;
    }

    facetStatus.textContent =
      event.target.value !==
      details.dataset.defaultSortBy
        ? event.target.dataset.optionName ?? ''
        : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define(
    'sorting-filter-component',
    SortingFilterComponent
  );
}


/* =========================================================
   FACET STATUS
========================================================= */

class FacetStatusComponent extends Component {

  updateListSummary(
    checkedInputElements
  ) {

    const count =
      checkedInputElements.length;

    this.getAttribute(
      'facet-type'
    ) === 'swatches'
      ? this.#updateSwatchSummary(
          checkedInputElements,
          count
        )
      : this.#updateBubbleSummary(
          checkedInputElements,
          count
        );
  }

  #updateSwatchSummary(
    checkedInputElements,
    count
  ) {

    const {
      facetStatus
    } = this.refs;

    facetStatus.classList.remove(
      'bubble',
      'facets__bubble'
    );

    if (count === 0) {

      facetStatus.innerHTML = '';

      return;
    }

    if (count > 3) {

      facetStatus.innerHTML =
        count.toString();

      facetStatus.classList.add(
        'bubble',
        'facets__bubble'
      );

      return;
    }

    facetStatus.innerHTML =
      Array.from(
        checkedInputElements
      )
        .map(
          (inputElement) => {

            const swatch =
              inputElement.parentElement
                ?.querySelector(
                  'span.swatch'
                );

            const span =
              document.createElement(
                'span'
              );

            span.className =
              'visually-hidden';

            span.textContent =
              inputElement.getAttribute(
                'aria-label'
              ) ?? '';

            return (
              swatch?.outerHTML ?? ''
            ) +
            span.outerHTML;
          }
        )
        .join('');
  }

  #updateBubbleSummary(
    checkedInputElements,
    count
  ) {

    const {
      facetStatus
    } = this.refs;

    const filterStyle =
      this.dataset.filterStyle;

    facetStatus.classList.remove(
      'bubble',
      'facets__bubble'
    );

    if (count === 0) {

      facetStatus.innerHTML = '';

      return;
    }

    if (
      filterStyle === 'horizontal' &&
      count === 1
    ) {

      facetStatus.textContent =
        checkedInputElements[0]
          ?.dataset.label ?? '';

      return;
    }

    facetStatus.innerHTML =
      count.toString();

    facetStatus.classList.add(
      'bubble',
      'facets__bubble'
    );
  }

  updatePriceSummary(
    minInput,
    maxInput
  ) {

    const minInputValue =
      minInput.value;

    const maxInputValue =
      maxInput.value;

    const {
      facetStatus
    } = this.refs;

    if (
      !minInputValue &&
      !maxInputValue
    ) {

      facetStatus.innerHTML =
        '';

      return;
    }

    const currency =
      facetStatus.dataset.currency ||
      '';

    const minInputNum =
      this.#parseCents(
        minInputValue,
        '0',
        currency
      );

    const maxInputNum =
      this.#parseCents(
        maxInputValue,
        facetStatus.dataset.rangeMax,
        currency
      );

    facetStatus.innerHTML =
      `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  #parseCents(
    value,
    fallback = '0',
    currency = ''
  ) {

    const result =
      convertMoneyToMinorUnits(
        value,
        currency
      );

    if (result !== null) {
      return result;
    }

    const fallbackResult =
      convertMoneyToMinorUnits(
        fallback,
        currency
      );

    if (fallbackResult !== null) {
      return fallbackResult;
    }

    const cleanFallback =
      fallback.replace(
        /[^\d]/g,
        ''
      );

    return (
      parseInt(
        cleanFallback,
        10
      ) || 0
    );
  }

  #formatMoney(moneyValue) {

    if (
      !(this.refs.moneyFormat instanceof HTMLTemplateElement)
    ) {
      return '';
    }

    const format =
      this.refs.moneyFormat.content
        .textContent ||
      '{{amount}}';

    const currency =
      this.refs.facetStatus.dataset.currency ||
      '';

    return formatMoney(
      moneyValue,
      format,
      currency
    );
  }

  clearSummary() {
    this.refs.facetStatus.innerHTML =
      '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define(
    'facet-status-component',
    FacetStatusComponent
  );
}

/* =========================================================
   RESULTS LIST HASH + SCROLL
========================================================= */

(function () {
  'use strict';

  const RESULTS_HASH = 'ResultsList';

  function addResultsHash() {
    const url = new URL(window.location.href);

    /*
     * Only add the hash when sort_by exists.
     *
     * Price/color filters are already handled by
     * the existing FacetsFormComponent logic.
     */
    if (!url.searchParams.has('sort_by')) {
      return;
    }

    if (url.hash !== `#${RESULTS_HASH}`) {
      url.hash = RESULTS_HASH;

      history.replaceState(
        history.state,
        '',
        url.toString()
      );
    }
  }

  function scrollToResults() {
    const results =
      document.getElementById(RESULTS_HASH);

    if (!results) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        results.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    });
  }

  /*
   * Handle page refresh / normal navigation.
   */
  function handlePageLoad() {
    const url = new URL(window.location.href);

    if (
      url.searchParams.has('sort_by') &&
      url.hash === `#${RESULTS_HASH}`
    ) {
      scrollToResults();
    }
  }

  /*
   * Handle browser back / forward.
   */
  window.addEventListener('popstate', () => {
    const url = new URL(window.location.href);

    if (url.hash === `#${RESULTS_HASH}`) {
      scrollToResults();
    }
  });

  /*
   * Give Shopify a moment to finish its sorting/navigation.
   */
  window.addEventListener('load', () => {
    setTimeout(() => {
      addResultsHash();
      handlePageLoad();
    }, 100);
  });

  /*
   * Also check shortly after the page has started.
   */
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      addResultsHash();
      handlePageLoad();
    }, 100);
  });
})();